import { formatCurrency } from '@angular/common';
import type * as L from 'leaflet';
import { CATEGORY_META, IBoondockingListing, IListing, IPoi, POI_KIND_META, TripStopKind, primaryPoiKind } from '@cnt-workspace/data-access';
import { symbolHtml } from './symbols/custom-symbols';

/**
 * Leaflet divIcon options for the listing / POI pins shared by the /search map
 * and the trip planner map, so both draw identical pins. Styles for these
 * classes are global (`apps/cnt-landing/src/styles.scss`). Callers wrap the
 * result in `L.divIcon(...)`.
 */

/** Price tag for a private listing; agency tag for a boondocking listing. */
export function listingPinIcon(listing: IListing): L.DivIconOptions {
  if ('agency' in listing) {
    return {
      html: `<div class="cnt-boondocking-marker" data-id="${listing.id}">
               <span class="material-symbols-outlined" style="font-variation-settings: 'FILL' 1;">landscape</span>
               <span>${agencyCode(listing.agency)}</span>
             </div>`,
      className: 'cnt-price-marker-wrap',
      iconSize: [76, 30],
      iconAnchor: [38, 30],
    };
  }
  const color = CATEGORY_META[listing.category].color;
  const price = formatCurrency(listing.price, 'en-US', '$', 'USD', '1.0-0');
  return {
    html: `<div class="cnt-price-marker" data-id="${listing.id}" style="--cat:${color}">${price}</div>`,
    className: 'cnt-price-marker-wrap',
    iconSize: [60, 30],
    iconAnchor: [30, 30],
  };
}

/** Rounded square in the POI's kind color with its kind icon. */
export function poiPinIcon(poi: IPoi): L.DivIconOptions {
  const meta = POI_KIND_META[primaryPoiKind(poi)];
  return {
    html: `
      <div style="position: relative; width: 28px; height: 28px;
                  border-radius: 8px; background: ${meta.color};
                  border: 2px solid #fff; box-shadow: 0 3px 8px rgba(0,0,0,0.25);
                  display: flex; align-items: center; justify-content: center;
                  color: #fff;">
        <span style="font-family: 'Material Symbols Outlined'; font-size: 16px; font-variation-settings: 'FILL' 1;">${symbolHtml(meta.icon)}</span>
      </div>`,
    className: 'cnt-poi-pin',
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  };
}

/** Marker color per trip stop kind — shared by the planner's stop lists and
 *  the trip markers on the /search and focused-planner maps. */
export const TRIP_STOP_KIND_COLOR: Record<TripStopKind, string> = {
  private: '#e3530d',
  boondocking: '#3b6e3b',
  poi: '#b3760e',
  custom: '#6b6b6b',
};

/** Corner badge on a trip stop pin: a flag for the start, a finish flag for
 *  the end, and the stop's number in between. */
export interface ITripStopBadge {
  text?: string;
  icon?: string;
  color: string;
}

export function tripStopBadge(index: number, count: number): ITripStopBadge | null {
  if (count < 2) return null;
  if (index === 0) return { icon: 'flag', color: '#295d42' };
  if (index === count - 1) return { icon: 'sports_score', color: '#9a3f0a' };
  return { text: String(index), color: '#222222' };
}

/** A POI trip stop on a map: the POI pin with its stop badge in the corner. */
export function poiStopPinIcon(poi: IPoi, badge: ITripStopBadge | null): L.DivIconOptions {
  const base = poiPinIcon(poi);
  const badgeHtml = !badge ? '' : `
    <div style="position:absolute;top:-8px;right:-8px;width:18px;height:18px;border-radius:50%;
                background:${badge.color};color:#fff;border:2px solid #fff;font-size:10px;font-weight:700;line-height:1;
                display:flex;align-items:center;justify-content:center;">${badge.icon
      ? `<span style="font-family:'Material Symbols Outlined';font-size:11px;font-variation-settings:'FILL' 1;">${badge.icon}</span>`
      : badge.text}</div>`;
  return { ...base, html: `<div style="position:relative;">${base.html}${badgeHtml}</div>` };
}

function agencyCode(agency: IBoondockingListing['agency'] | undefined): string {
  switch (agency) {
    case 'State Park':
      return 'STATE';
    case 'Army Corps':
      return 'COE';
    case 'County':
      return 'CTY';
    case 'Other':
      return 'PUB';
    case undefined:
      return 'BOON';
    default:
      return agency; // BLM / USFS / NPS
  }
}
